import { useState, useCallback, useEffect, useRef } from 'react';
import apiClient from '../../lib/api';

export type VoiceRecorderState = 'idle' | 'recording' | 'processing' | 'error';

export interface UseSpeechRecognitionResult {
  state: VoiceRecorderState;
  transcript: string;
  lastSegment: string;
  segmentId: number;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  resetRecording: () => void;
  updateTranscript: (text: string) => void;
  confirmTranscript: () => void;
}

const MAX_RECORDING_DURATION = 60_000; // 60 seconds
const SILENCE_THRESHOLD = 0.015; // RMS level below which is considered silence（小さな声の検出感度向上）
const SILENCE_DURATION = 5_000; // 5 seconds of silence after speech to auto-stop（長いポーズへの対応）
const SILENCE_CHECK_INTERVAL = 200; // Check audio level every 200ms
const NO_SPEECH_TIMEOUT = 3_000; // 3秒間音声未検出で自動停止（録音開始の猶予短縮）

// Module-level shared resources (survive component unmount)
let sharedStream: MediaStream | null = null;
let sharedAudioContext: AudioContext | null = null;
let sharedAnalyser: AnalyserNode | null = null;
let idleReleaseTimer: ReturnType<typeof setTimeout> | null = null;
const IDLE_RELEASE_MS = 60_000; // 1分間録音なしでマイク解放

function cancelIdleRelease() {
  if (idleReleaseTimer) {
    clearTimeout(idleReleaseTimer);
    idleReleaseTimer = null;
  }
}

function scheduleIdleRelease() {
  cancelIdleRelease();
  idleReleaseTimer = setTimeout(() => {
    if (sharedAudioContext) {
      void sharedAudioContext.close().catch(() => {});
      sharedAudioContext = null;
    }
    sharedAnalyser = null;
    if (sharedStream) {
      sharedStream.getTracks().forEach((t) => t.stop());
      sharedStream = null;
    }
  }, IDLE_RELEASE_MS);
}

async function getOrCreateStream(): Promise<MediaStream> {
  if (sharedStream && sharedStream.getTracks().every((t) => t.readyState === 'live')) {
    return sharedStream;
  }
  // 古い stream があればクリーンアップ
  if (sharedStream) {
    sharedStream.getTracks().forEach((t) => t.stop());
  }
  if (sharedAudioContext) {
    void sharedAudioContext.close().catch(() => {});
  }
  sharedAudioContext = null;
  sharedAnalyser = null;
  try {
    // 可能であればエコーキャンセル等を有効化（対応していない端末では自動的に無視される）
    sharedStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
      },
    });
  } catch (err) {
    // 古い端末・ブラウザなどで制約付き取得に失敗した場合は、シンプルな audio: true でフォールバック
    console.warn(
      '高度な音声処理付きでのマイク取得に失敗したため、フォールバックを使用します',
      err,
    );
    sharedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  }
  return sharedStream;
}

function getOrCreateAudioNodes(stream: MediaStream): {
  audioContext: AudioContext;
  analyser: AnalyserNode;
} {
  if (sharedAudioContext && sharedAnalyser) {
    if (sharedAudioContext.state === 'suspended') {
      sharedAudioContext.resume();
    }
    return { audioContext: sharedAudioContext, analyser: sharedAnalyser };
  }
  const audioContext = new AudioContext();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);
  sharedAudioContext = audioContext;
  sharedAnalyser = analyser;
  return { audioContext, analyser };
}

function getSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;

  // Preferred MIME types in order
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  // Fallback: let the browser choose
  return '';
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      // Strip the data URL prefix (e.g., "data:audio/webm;base64,")
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const useSpeechRecognition = (): UseSpeechRecognitionResult => {
  const [state, setState] = useState<VoiceRecorderState>('idle');
  const [transcript, setTranscript] = useState('');
  const [lastSegment, setLastSegment] = useState('');
  const [segmentId, setSegmentId] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('');
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const speechDetectedRef = useRef(false);
  const silenceCheckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef(0);
  const recordingStartRef = useRef<number | null>(null);

  // Check browser support (MediaRecorder + getUserMedia)
  const isSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  // Stop recording session but keep module-level stream/AudioContext for reuse
  const stopSession = useCallback(() => {
    if (silenceCheckTimerRef.current) {
      clearInterval(silenceCheckTimerRef.current);
      silenceCheckTimerRef.current = null;
    }
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    silenceStartRef.current = null;
    speechDetectedRef.current = false;
    recordingStartRef.current = null;
    scheduleIdleRelease();
  }, []);

  // Release local resources only (unmount) — module-level stream/AudioContext are preserved
  const releaseAllResources = useCallback(() => {
    stopSession();
    chunksRef.current = [];
  }, [stopSession]);

  const sendToSTT = useCallback(async (blob: Blob, mimeType: string) => {
    const currentSession = sessionIdRef.current;
    setState('processing');
    try {
      const base64 = await blobToBase64(blob);
      const result = await apiClient.speechToText(base64, mimeType);

      // セッションが変わっていたら結果を破棄（reset/新規録音が発生した）
      if (sessionIdRef.current !== currentSession) return;

      const text = result.transcript || '';

      if (text.trim()) {
        setTranscript((prev) => prev + text);
        setLastSegment(text);
        setSegmentId((prev) => prev + 1);
      } else {
        setError('音声が検出されませんでした。もう一度お試しください。');
        setState('error');
        return;
      }

      setState('idle');
    } catch (e: any) {
      // セッションが変わっていたらエラーも破棄
      if (sessionIdRef.current !== currentSession) return;

      console.error('STT error:', e);
      const message =
        e?.message || e?.error || '音声認識に失敗しました。もう一度お試しください。';
      setError(message);
      setState('error');
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('お使いのブラウザは音声聞き取りに対応していません。');
      setState('error');
      return;
    }

    // 新しいセッションを開始（進行中のSTT結果を無効化）
    sessionIdRef.current += 1;

    // Reset state
    setTranscript('');
    setLastSegment('');
    setError(null);
    chunksRef.current = [];

    const mimeType = getSupportedMimeType();
    if (mimeType === null) {
      setError('お使いのブラウザは音声聞き取りに対応していません。');
      setState('error');
      return;
    }
    mimeTypeRef.current = mimeType;

    try {
      cancelIdleRelease();
      const stream = await getOrCreateStream();

      const options: MediaRecorderOptions = {};
      if (mimeType) {
        options.mimeType = mimeType;
      }

      // MediaRecorder must be created fresh each time (cannot reuse after stop)
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Clear silence detection interval
        if (silenceCheckTimerRef.current) {
          clearInterval(silenceCheckTimerRef.current);
          silenceCheckTimerRef.current = null;
        }

        if (maxDurationTimerRef.current) {
          clearTimeout(maxDurationTimerRef.current);
          maxDurationTimerRef.current = null;
        }

        const chunks = chunksRef.current;
        if (chunks.length === 0) {
          setError('音声が聞き取れませんでした。');
          setState('error');
          scheduleIdleRelease();
          return;
        }

        if (!speechDetectedRef.current) {
          chunksRef.current = [];
          setError('音声が検出されませんでした。もう一度お試しください。');
          setState('error');
          scheduleIdleRelease();
          return;
        }

        const actualMimeType = recorder.mimeType || mimeTypeRef.current || 'audio/webm';
        const blob = new Blob(chunks, { type: actualMimeType });
        chunksRef.current = [];

        sendToSTT(blob, actualMimeType);
        scheduleIdleRelease();
      };

      recorder.onerror = () => {
        stopSession();
        setError('聞き取り中にエラーが発生しました。');
        setState('error');
      };

      const { analyser } = getOrCreateAudioNodes(stream);

      speechDetectedRef.current = false;
      silenceStartRef.current = null;
      const dataArray = new Uint8Array(analyser.fftSize);

      silenceCheckTimerRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const normalized = (dataArray[i] - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / dataArray.length);

        if (rms > SILENCE_THRESHOLD) {
          speechDetectedRef.current = true;
          silenceStartRef.current = null;
        } else if (speechDetectedRef.current) {
          if (silenceStartRef.current === null) {
            silenceStartRef.current = Date.now();
          } else if (Date.now() - silenceStartRef.current >= SILENCE_DURATION) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
          }
        } else if (!speechDetectedRef.current && recordingStartRef.current) {
          if (Date.now() - recordingStartRef.current >= NO_SPEECH_TIMEOUT) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
          }
        }
      }, SILENCE_CHECK_INTERVAL);

      recordingStartRef.current = Date.now();
      recorder.start();
      setState('recording');

      // Max duration timer
      maxDurationTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_DURATION);
    } catch (e: any) {
      console.error('Failed to start recording:', e);
      // ローカルリソースのみクリーンアップ
      stopSession();
      chunksRef.current = [];

      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        // 許可拒否時は module-level もクリーンアップ（stream 取得不可のため）
        if (sharedAudioContext) {
          void sharedAudioContext.close().catch(() => {});
          sharedAudioContext = null;
        }
        sharedAnalyser = null;
        if (sharedStream) {
          sharedStream.getTracks().forEach((t) => t.stop());
          sharedStream = null;
        }
        setError('マイクの使用が許可されていません。ブラウザの設定を確認してください。');
      } else if (e.name === 'NotFoundError') {
        setError('マイクが見つかりません。マイクの設定を確認してください。');
      } else {
        setError('聞き取りを開始できませんでした。');
      }
      setState('error');
    }
  }, [isSupported, stopSession, sendToSTT]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      // onstop handler will trigger sendToSTT
    }
  }, []);

  const resetRecording = useCallback(() => {
    // 進行中のSTT結果を無効化
    sessionIdRef.current += 1;
    // Clear chunks before stopSession to prevent async onstop from sending to STT
    chunksRef.current = [];
    stopSession();
    setTranscript('');
    setLastSegment('');
    setError(null);
    setState('idle');
  }, [stopSession]);

  const updateTranscript = useCallback((text: string) => {
    setTranscript(text);
  }, []);

  const confirmTranscript = useCallback(() => {
    setState('idle');
  }, []);

  // Release all resources on unmount
  useEffect(() => {
    return () => {
      releaseAllResources();
    };
  }, [releaseAllResources]);

  return {
    state,
    transcript,
    lastSegment,
    segmentId,
    interimTranscript: '', // Always empty (backward compatibility)
    error,
    isSupported,
    startRecording,
    stopRecording,
    resetRecording,
    updateTranscript,
    confirmTranscript,
  };
};

export default useSpeechRecognition;
